import getEntry from '../model/contentful/entry-get';
import createEntry from '../model/contentful/entry-create';
import entryResolve from '../model/contentful/link-resolve';
import publishEntry from '../model/contentful/entry-publish';
import unpublishEntry from '../model/contentful/entry-unpublish';
import updateEntry from '../model/contentful/entry-update';
import archiveEntry from '../model/contentful/entry-archive';
import unarchiveEntry from '../model/contentful/entry-unarchive';

class EntryService {
  constructor(space, transformer) {
    this.space = space;
    this.transformer = transformer;
  }

  getById(id, depth = 0, raw = false, fieldsToResolve) {
    return getEntry(this.space, id).then((entry) => {
      if (raw) {
        return entry;
      }

      return entryResolve(this.space, entry, depth, fieldsToResolve).then((entry) => {
        return this.transformer.contentfulToInternal(entry);
      });
    });
  }

  create(type, instance, depth = 0) {
    const data = this.transformer.internalToContentful(instance);

    return createEntry(this.space, type, data).then((entry) => {
      return entryResolve(this.space, entry, depth);
    }).then((entry) => {
      return this.transformer.contentfulToInternal(entry);
    });
  }

  initializeCustom(type, depth = 0) {
    // Fetch a random item so we get the fields
    return this.space.getEntries({
      content_type: type,
      limit: 1,
      order: '-sys.createdAt'
    }).then((entries) => {
      const data = entries.toPlainObject();
      return entryResolve(this.space, data, depth);
    }).then((data) => {
      // Remove the fields from the random fetched item
      data.items[0].fields = {};
      return data.items.map(this.transformer.contentfulToInternal);
    }).then((data) => {
      // Remove links to existing random item
      // Note: we do all this instead of using a createEmpty() thing in transformer to prevent the maintenance overhead
      delete data[0].id;
      delete data[0].version;
      delete data[0].published;
      delete data[0].publishedCounter;
      delete data[0].updated;
      delete data[0].draft;
      delete data[0].archived;
      delete data[0].created;

      data[0].custom = true;
      return data[0];
    });
  }

  save(instance, depth = 0) {
    const data = this.transformer.internalToContentful(instance);

    return getEntry(this.space, instance.id).then((entry) => {
      return updateEntry(entry, data);
    }).then((entry) => {
      return entryResolve(this.space, entry, depth);
    }).then((entry) => {
      return this.transformer.contentfulToInternal(entry);
    });
  }

  publish(id, depth = 0) {
    return getEntry(this.space, id).then((entry) => {
      return publishEntry(entry);
    }).then((entry) => {
      return entryResolve(this.space, entry, depth);
    }).then((entry) => {
      return this.transformer.contentfulToInternal(entry);
    });
  }

  unpublish(id, depth = 0) {
    return getEntry(this.space, id).then((entry) => {
      return unpublishEntry(entry);
    }).then((entry) => {
      return entryResolve(this.space, entry, depth);
    }).then((entry) => {
      return this.transformer.contentfulToInternal(entry);
    });
  }

  archive(id, depth = 0) {
    return getEntry(this.space, id).then((entry) => {
      return archiveEntry(entry);
    }).then((entry) => {
      return entryResolve(this.space, entry, depth);
    }).then((entry) => {
      return this.transformer.contentfulToInternal(entry);
    });
  }

  unarchive(id, depth = 0) {
    return getEntry(this.space, id).then((entry) => {
      return unarchiveEntry(entry);
    }).then((entry) => {
      return entryResolve(this.space, entry, depth);
    }).then((entry) => {
      return this.transformer.contentfulToInternal(entry);
    });
  }
}

export default EntryService;
